import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../auth";
import { isCapacitorNative } from "../lib/pwaCamera";

/**
 * Sign in with Google — one tap, no form.
 *
 * Someone who has just found an injured cat will not stop to invent a password.
 * They will put the phone away, and the cat loses. This is the shortest path
 * from "I need to report this" to "reported".
 *
 * Two runtimes, one contract: both hand us a Google ID token, and the backend
 * verifies it with Google before it trusts a single claim inside.
 *   • Web      — Google Identity Services renders the button and returns a
 *                credential in its callback.
 *   • Native   — the Capacitor plugin opens the OS account picker (no browser
 *                round-trip, no re-typing an address the phone already knows).
 *
 * Requires REACT_APP_GOOGLE_CLIENT_ID (the *web* client ID). Without it we
 * render nothing rather than a button that dead-ends — a broken sign-in option
 * is worse than one that was never offered.
 */
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
// iOS needs its own client ID for the account picker, on top of the web one it
// still verifies against. Optional: without it the Android and web flows are
// unaffected, and iOS simply falls back to the web path.
const IOS_CLIENT_ID = process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID || "";
const GSI_SRC = "https://accounts.google.com/gsi/client";

/**
 * True only inside the packaged iOS app.
 *
 * Not "is this an iPhone" — Safari on an iPhone is the web, and the web is not
 * the App Store's business. The question is whether this code is running inside
 * the binary Apple reviews.
 */
function isIOSNative() {
  if (typeof window === "undefined") return false;
  try {
    return (
      isCapacitorNative() && window.Capacitor?.getPlatform?.() === "ios"
    );
  } catch {
    return false;
  }
}

/**
 * Will the Google button actually render anything?
 *
 * The pages that host it also draw a "veya / or" divider beneath it. A divider
 * with nothing above it is a bug the user can see, so the page needs to ask the
 * same question the button asks itself — from one place, so the two answers can
 * never drift apart.
 */
export function isGoogleSignInAvailable() {
  return Boolean(GOOGLE_CLIENT_ID) && !isIOSNative();
}

function loadGsiScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.querySelector(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default function GoogleSignInButton({ isTr = true, onSuccess = null }) {
  const { loginWithGoogle } = useAuth();
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const buttonRef = useRef(null);

  // Take the native path only when the native plugin is ACTUALLY THERE.
  //
  // `isCapacitorNative()` alone is not enough: Capacitor's runtime ships in the
  // web bundle too, so it reports true in a plain browser — where the plugin
  // does not exist. The button then chose the native path, found no plugin, and
  // failed instantly with "could not sign in", never reaching Google at all.
  // Ask the question that actually matters: *can I call it?*
  const native =
    isCapacitorNative() &&
    typeof window.Capacitor?.Plugins?.SocialLogin?.login === "function";

  const handleToken = async (idToken) => {
    if (!idToken) return;
    setBusy(true);
    try {
      const user = await loginWithGoogle(idToken);
      onSuccess?.(user);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        (typeof detail === "string" && detail) ||
          (isTr ? "Google ile giriş yapılamadı." : "Could not sign in with Google."),
      );
    } finally {
      setBusy(false);
    }
  };

  // ── Web: Google Identity Services ────────────────────────────────────────
  useEffect(() => {
    if (native || !GOOGLE_CLIENT_ID) return;
    let cancelled = false;

    loadGsiScript()
      .then(() => {
        if (cancelled || !buttonRef.current) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => handleToken(response?.credential),
        });
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: "outline",
          size: "large",
          shape: "pill",
          text: "continue_with",
          width: 320,
          locale: isTr ? "tr" : "en",
        });
        setReady(true);
      })
      .catch(() => {
        // Offline, blocked, or Google is down. Say nothing and leave the
        // email/password form standing — it still works.
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [native, isTr]);

  // ── Native: the OS account picker ────────────────────────────────────────
  //
  // Google refuses OAuth inside an embedded WebView (`disallowed_useragent`),
  // so the web flow above cannot work in the packaged app — the store build
  // *needs* this path. It goes through @capgo/capacitor-social-login, which is
  // the one Google plugin still tracking current Capacitor: the older
  // @codetrix-studio plugin is archived and pinned to Capacitor 6.
  //
  // Reached through Capacitor's runtime plugin registry rather than a static
  // import. Webpack resolves dynamic imports at build time, so naming the
  // package here would break the web build on any machine without the native
  // plugin — and the web build never needs it. The registry is empty in a
  // browser and populated on device, which is exactly the distinction we want.
  const signInNative = async () => {
    setBusy(true);
    try {
      const SocialLogin = window.Capacitor?.Plugins?.SocialLogin;
      if (!SocialLogin?.login) throw new Error("plugin unavailable");

      // Initialise with the WEB client ID on BOTH platforms. Google requires it
      // that way on Android, and it is the audience the backend verifies the
      // token against — handing it the iOS/Android client ID instead yields a
      // token the server correctly rejects.
      await SocialLogin.initialize({
        google: {
          webClientId: GOOGLE_CLIENT_ID,
          ...(IOS_CLIENT_ID ? { iOSClientId: IOS_CLIENT_ID, iOSServerClientId: GOOGLE_CLIENT_ID } : {}),
          mode: "online",
        },
      });

      // @capgo social-login on ANDROID throws "You CANNOT use scopes without
      // modifying the main activity" the moment any scopes are passed. profile +
      // email are the default grant anyway and all the backend needs is the
      // idToken, so on Android we omit scopes entirely. iOS is left exactly as
      // it shipped (it accepts scopes and is already in review).
      const isAndroid = window.Capacitor?.getPlatform?.() === "android";
      const res = await SocialLogin.login({
        provider: "google",
        ...(isAndroid ? {} : { options: { scopes: ["profile", "email"] } }),
      });

      const idToken = res?.result?.idToken;
      if (!idToken) throw new Error("no id token");
      await handleToken(idToken);
    } catch (err) {
      // The user closing the picker is not an error worth shouting about.
      const msg = String(err?.message || err || "");
      if (!/cancel|closed|dismiss/i.test(msg)) {
        toast.error(
          isTr ? "Google ile giriş yapılamadı." : "Could not sign in with Google.",
        );
      }
      setBusy(false);
    }
  };

  if (!GOOGLE_CLIENT_ID) return null;

  // ── App Store Guideline 4.8 ──────────────────────────────────────────────
  //
  // If an app offers a third-party sign-in (Google) to set up the user's
  // PRIMARY account, Apple requires an equivalent privacy-focused option —
  // in practice, Sign in with Apple. The exception is apps that use ONLY
  // their own account system, which is not us: we offer both.
  //
  // We do not have Sign in with Apple yet, and the submission will not wait
  // for it. So on iOS we show only the email/password form we own. Google
  // stays on web and Android, where the rule does not apply.
  //
  // DELETE THIS BLOCK the day Sign in with Apple ships. It is a stay of
  // execution, not a decision.
  if (isIOSNative()) return null;

  if (native) {
    return (
      <button
        type="button"
        onClick={signInNative}
        disabled={busy}
        data-testid="google-signin-native"
        className="w-full h-12 rounded-full border border-stone-300 bg-white text-stone-800 font-semibold flex items-center justify-center gap-2.5 disabled:opacity-60 active:bg-stone-50"
      >
        <GoogleMark />
        {busy
          ? isTr
            ? "Giriş yapılıyor…"
            : "Signing in…"
          : isTr
          ? "Google ile devam et"
          : "Continue with Google"}
      </button>
    );
  }

  return (
    <div className="w-full flex justify-center" data-testid="google-signin-web">
      <div ref={buttonRef} className={busy ? "opacity-60 pointer-events-none" : ""} />
      {!ready && (
        <div className="h-12 grid place-items-center text-xs text-stone-400">
          {isTr ? "Google yükleniyor…" : "Loading Google…"}
        </div>
      )}
    </div>
  );
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}
